// PDF user space units per CSS pixel (72 pt per inch / 96 px per inch)
const PX_TO_PT = 72 / 96;

const encoder = new TextEncoder();

const toBytes = (part: string | Uint8Array) =>
  typeof part === 'string' ? encoder.encode(part) : part;

const round = (value: number) => Math.round(value * 100) / 100;

/**
 * Portrait page sizes in PDF points (1/72 inch)
 */
export const PDF_PAGE_SIZES = {
  A4: [595.28, 841.89],
} satisfies Record<string, [number, number]>;

export interface CreatePdfFromJpegOptions {
  /**
   * Page size in points, as [width, height]. It is rotated to match the image
   * orientation. When omitted, the page takes the size of the image (at 96 dpi)
   */
  pageSize?: [number, number];
  /**
   * Minimum space in points left between the image and the page edges when a
   * `pageSize` is given
   */
  margin?: number;
}

/**
 * Wrap a JPEG image into a single-page PDF document.
 *
 * With a `pageSize`, the page is oriented like the image (landscape for wide
 * images) and the image is scaled to fit inside the margins, centered and
 * keeping its aspect ratio. Otherwise the page has the size of the image.
 *
 * The JPEG bytes are embedded as-is (`DCTDecode`), so the image is not
 * re-encoded and no PDF library is needed.
 */
export const createPdfFromJpeg = (
  jpeg: Uint8Array,
  width: number,
  height: number,
  { pageSize, margin = 0 }: CreatePdfFromJpegOptions = {}
): Uint8Array => {
  let pageWidth = width * PX_TO_PT;
  let pageHeight = height * PX_TO_PT;
  let imageWidth = pageWidth;
  let imageHeight = pageHeight;

  if (pageSize) {
    const [short, long] = [...pageSize].sort((a, b) => a - b);
    [pageWidth, pageHeight] = width > height ? [long, short] : [short, long];

    const scale = Math.min((pageWidth - 2 * margin) / width, (pageHeight - 2 * margin) / height);
    imageWidth = width * scale;
    imageHeight = height * scale;
  }

  const x = round((pageWidth - imageWidth) / 2);
  const y = round((pageHeight - imageHeight) / 2);
  pageWidth = round(pageWidth);
  pageHeight = round(pageHeight);
  const content = `q ${round(imageWidth)} 0 0 ${round(imageHeight)} ${x} ${y} cm /Im0 Do Q`;

  const objects: Array<Array<string | Uint8Array>> = [
    ['<< /Type /Catalog /Pages 2 0 R >>'],
    ['<< /Type /Pages /Kids [3 0 R] /Count 1 >>'],
    [
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] ` +
        '/Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>',
    ],
    [
      `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} ` +
        '/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ' +
        `/Length ${jpeg.length} >>\nstream\n`,
      jpeg,
      '\nendstream',
    ],
    [`<< /Length ${content.length} >>\nstream\n${content}\nendstream`],
  ];

  const chunks: Uint8Array[] = [];
  let offset = 0;
  const push = (part: string | Uint8Array) => {
    const bytes = toBytes(part);
    chunks.push(bytes);
    offset += bytes.length;
  };

  // header + binary marker comment so tools treat the file as binary
  push('%PDF-1.4\n');
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  const offsets: number[] = [];
  objects.forEach((parts, index) => {
    offsets.push(offset);
    push(`${index + 1} 0 obj\n`);
    parts.forEach(push);
    push('\nendobj\n');
  });

  const xrefOffset = offset;
  push(
    `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n` +
      offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')
  );
  push(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`);
  push(`startxref\n${xrefOffset}\n%%EOF\n`);

  const pdf = new Uint8Array(offset);
  let position = 0;
  for (const chunk of chunks) {
    pdf.set(chunk, position);
    position += chunk.length;
  }

  return pdf;
};

export default createPdfFromJpeg;
